import { Injectable, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IQBOOST_V2_FEATURE_FLAG, isGlobalServersInEffect } from '@maximizer/ai/data-access';
import { FeatureService } from '@maximizer/core/shared/data-access';
import { OutlookStore } from '@maximizer/outlook/shared/data-access';
import { isAdminAnonymizationEnabled } from '../utils/data-processing-config';

@Injectable()
export class OutlookIqBoostPrivacyService {
  private readonly featureService = inject(FeatureService);

  private readonly dataProcessingFeatures = computed(() => {
    const admin = this.outlookStore.adminDataProcessingFeatures();
    return {
      anonymized:
        isAdminAnonymizationEnabled(admin) ||
        this.outlookStore.userAnonymizationEnabled(),
      useWebData:
        (admin?.useWebData ?? false) && this.outlookStore.userWebDataEnabled(),
      useGlobalServers: isGlobalServersInEffect(
        admin,
        this.outlookStore.userGlobalServersEnabled(),
        this.featureService.isFeatureOn(IQBOOST_V2_FEATURE_FLAG, false),
      ),
    };
  });

  readonly anonymizerEnabled = computed(
    () => this.dataProcessingFeatures().anonymized,
  );
  readonly dataSourceEnabled = computed(
    () => !this.dataProcessingFeatures().useWebData,
  );
  readonly serverRegionEnabled = computed(
    () => !this.dataProcessingFeatures().useGlobalServers,
  );

  readonly anonymizerStatus = computed(() =>
    this.translate.instant(
      this.anonymizerEnabled()
        ? 'outlook.iqBoost.privacySecurity.anonymizerActive'
        : 'outlook.iqBoost.privacySecurity.anonymizerInactive',
    ),
  );
  readonly dialogDataSource = computed(() =>
    this.translate.instant(
      this.dataSourceEnabled()
        ? 'outlook.iqBoost.privacySecurity.maximizerOnly'
        : 'outlook.iqBoost.privacySecurity.webData',
    ),
  );
  readonly dialogServerRegion = computed(() =>
    this.translate.instant(
      this.serverRegionEnabled()
        ? 'outlook.iqBoost.privacySecurity.canadaServers'
        : 'outlook.iqBoost.privacySecurity.globalServers',
    ),
  );

  constructor(
    private readonly outlookStore: OutlookStore,
    private readonly translate: TranslateService,
  ) {}
}
